import { useEffect, useMemo, useRef } from 'react';
import { useStorage } from '@funtech-int/web-toolkit';

import { updateSubscriptionPurchaseSuppressedSession } from '@/shared/lib/profileSession';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { useLogger } from '@/shared/hooks/useLogger';

export function useSubscriptionRefetch() {
    const logger = useLogger();
    const storage = useStorage();
    const profileManager = useProfileManager();
    const abortControllerRef = useRef<AbortController | null>(null);

    const controller = useMemo(
        () => ({
            async refetch() {
                abortControllerRef.current?.abort();
                const controller = new AbortController();
                abortControllerRef.current = controller;

                updateSubscriptionPurchaseSuppressedSession(storage);

                try {
                    await profileManager.updateSubscriptionInfo({ abortSignal: controller.signal });
                } catch (error: unknown) {
                    if ((error as Error).name === 'AbortError') return;

                    logger.error({ error }, 'fetchUserSubscriptionInfo error');
                }
            },
            cleanUp() {
                abortControllerRef.current?.abort();
            },
        }),
        [storage, profileManager, logger]
    );

    useEffect(() => controller.cleanUp, [controller]);

    return controller;
}
